import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  Box,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { type ComponentProps, useMemo, useState } from 'react';
import { Document, Page } from 'react-pdf';

import { ZoomControls } from '~/components/documentReview/ZoomControls';
import type { InvoiceViewerDocument } from '~/components/invoices/detailPage/invoiceDocuments';

interface InvoiceDocumentViewerProps {
  /** The invoice's own document(s) — what the viewer shows by default. */
  primaryDocuments: InvoiceViewerDocument[];
  /** Supporting data and ticket attachments, reachable from the switcher. */
  supportingDocuments: InvoiceViewerDocument[];
  isLoading: boolean;
  emptyMessage?: string;
  sx?: SxProps<Theme>;
}

const EmptyState = ({ message }: { message: string }) => (
  <Stack alignItems="center" justifyContent="center" spacing={1} height="100%" p={3}>
    <DescriptionOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
    <Typography variant="body2" color="text.secondary" textAlign="center">
      {message}
    </Typography>
  </Stack>
);

/**
 * The left-hand pane of the invoice detail page: the invoice's document as one
 * continuous scroll. Only the pages the selected record owns are rendered — a source
 * PDF from a multi-document upload holds several invoices.
 */
export function InvoiceDocumentViewer({
  primaryDocuments,
  supportingDocuments,
  isLoading,
  emptyMessage = 'No document attached to this invoice.',
  sx,
}: InvoiceDocumentViewerProps) {
  // One source file can reach the viewer from more than one place — linked to two
  // tickets, or to a ticket and to the invoice as supporting data — and each route
  // produces its own entry for the same `attachmentId`. Duplicate ids break the
  // switcher's keys, and `find` below would only ever resolve the first.
  //
  // Merge rather than keep the first: the entries carry different page lists, because
  // each is built from the pages *that* link owns. A five-page upload with one voided
  // page arrives as a primary entry for pages 1 and 3 and a supporting entry for page
  // 2, and dropping the second one made page 2 unreachable. The label stays the first
  // one in, which keeps the invoice's own document named ahead of the copy reached via
  // a ticket.
  const documents = useMemo(() => {
    const byId = new Map<string, InvoiceViewerDocument>();
    for (const doc of [...primaryDocuments, ...supportingDocuments]) {
      const existing = byId.get(doc.id);
      if (existing) {
        existing.pageNumbers = [...new Set([...existing.pageNumbers, ...doc.pageNumbers])].sort(
          (a, b) => a - b,
        );
      } else {
        byId.set(doc.id, { ...doc, pageNumbers: [...doc.pageNumbers] });
      }
    }
    return [...byId.values()];
  }, [primaryDocuments, supportingDocuments]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Fall back to the first primary document until the reader picks something,
  // and again if the selected one disappears (e.g. an attachment was deleted).
  const selected = documents.find((doc) => doc.id === selectedId) ?? documents[0];

  // An empty page list means the whole file, counted once it loads.
  const [loadedPages, setLoadedPages] = useState<{ id: string; count: number } | null>(null);
  const pageNumbers =
    selected && selected.pageNumbers.length === 0
      ? loadedPages?.id === selected.id
        ? Array.from({ length: loadedPages.count }, (_, index) => index + 1)
        : []
      : (selected?.pageNumbers ?? []);

  const [baseWidth, setBaseWidth] = useState<number | undefined>(undefined);
  const [scale, setScale] = useState(1);
  const scaledWidth = baseWidth ? baseWidth * scale : undefined;

  // Size to the first rendered page, read off react-pdf's own `onLoadSuccess` rather
  // than calling `pdf.getPage()` ourselves: react-pdf owns the page load lifecycle and
  // cancels it on unmount, so a viewer torn down mid-load can't leave a rejected promise
  // dangling.
  const handleFirstPageLoad: NonNullable<ComponentProps<typeof Page>['onLoadSuccess']> = (page) => {
    const pageWidth = page.view[2] - page.view[0];
    if (pageWidth) setBaseWidth(pageWidth);
  };

  if (isLoading) {
    return (
      <Box sx={sx} p={2}>
        <Skeleton variant="rectangular" height="100%" sx={{ minHeight: 320 }} />
      </Box>
    );
  }

  if (!selected) {
    return (
      <Box sx={sx}>
        <EmptyState message={emptyMessage} />
      </Box>
    );
  }

  return (
    <Box sx={sx} display="flex" flexDirection="column" minHeight={0} position="relative">
      {documents.length > 1 ? (
        <Box px={2} pt={2} pb={1}>
          <Select
            size="small"
            fullWidth
            value={selected.id}
            onChange={(event) => setSelectedId(event.target.value)}
            inputProps={{ 'aria-label': 'Document to view' }}
          >
            {documents.map((doc) => (
              <MenuItem key={doc.id} value={doc.id}>
                {doc.label}
                {doc.pageNumbers.length > 1 ? ` (${doc.pageNumbers.length} pages)` : ''}
              </MenuItem>
            ))}
          </Select>
        </Box>
      ) : null}

      <Box flex={1} minHeight={0} sx={{ overflow: 'auto', px: 2, pb: 2 }}>
        <Document
          // Remount on file change so a stale page list can't paint over the new
          // document while it loads.
          key={selected.id}
          file={selected.filePath}
          loading={<Skeleton variant="rectangular" height={320} />}
          error={<EmptyState message="This document could not be loaded." />}
          onLoadSuccess={({ numPages }) => setLoadedPages({ id: selected.id, count: numPages })}
        >
          <Stack spacing={2} alignItems="center">
            {pageNumbers.map((pageNumber, index) => (
              <Paper key={pageNumber} elevation={2} sx={{ overflow: 'hidden' }}>
                <Page
                  pageNumber={pageNumber}
                  width={scaledWidth}
                  onLoadSuccess={index === 0 ? handleFirstPageLoad : undefined}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </Paper>
            ))}
          </Stack>
        </Document>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          pb: 2,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <ZoomControls scale={scale} onScaleChange={setScale} />
      </Box>
    </Box>
  );
}
